import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Search, CheckCircle2, AlertCircle, XCircle, Lightbulb, Coins, Link2, Unlink, Star, MessageSquare, Send, Calendar, Clock, Pencil, Trash2, PenTool, ExternalLink, RefreshCw } from 'lucide-react';
import GBPPhoneSetup from './GBPPhoneSetup';

const API = process.env.REACT_APP_BACKEND_URL;

const GRADE_COLORS = {
  A: 'from-green-500 to-emerald-600', B: 'from-blue-500 to-cyan-600',
  C: 'from-yellow-500 to-amber-600', D: 'from-orange-500 to-red-500', F: 'from-red-600 to-red-800',
};
const STATUS_ICON = { complete: CheckCircle2, partial: AlertCircle, missing: XCircle };
const STATUS_COLOR = { complete: 'text-green-500', partial: 'text-yellow-500', missing: 'text-red-500' };

const POST_TYPES = [
  { value: 'STANDARD', label: 'Update' },
  { value: 'OFFER', label: 'Offer' },
  { value: 'EVENT', label: 'Event' },
];
const CTA_TYPES = [
  { value: '', label: 'No CTA' },
  { value: 'LEARN_MORE', label: 'Learn More' },
  { value: 'BOOK', label: 'Book' },
  { value: 'ORDER', label: 'Order Online' },
  { value: 'SHOP', label: 'Shop' },
  { value: 'SIGN_UP', label: 'Sign Up' },
  { value: 'CALL', label: 'Call' },
];
const POST_STATUS_STYLES = {
  scheduled: 'bg-blue-50 text-blue-700 border-blue-200',
  published: 'bg-green-50 text-green-700 border-green-200',
  failed: 'bg-red-50 text-red-700 border-red-200',
  cancelled: 'bg-gray-100 text-gray-500 border-gray-200',
};

export default function ClientGBPAudit({ client, getHeaders }) {
  const { t } = useTranslation('dashboard');
  const [audit, setAudit] = useState(null);
  const [pastAudits, setPastAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [tab, setTab] = useState('audit');
  // GBP connection state
  const [gbpStatus, setGbpStatus] = useState(null);
  const [gbpReviews, setGbpReviews] = useState(null);
  const [loadingGbp, setLoadingGbp] = useState(true);
  // Posts state
  const [scheduledPosts, setScheduledPosts] = useState([]);
  const [postForm, setPostForm] = useState({ summary: '', post_type: 'STANDARD', cta_type: '', cta_url: '', scheduled_for: '' });
  const [posting, setPosting] = useState(false);
  const [editingPost, setEditingPost] = useState(null);

  const fetchGbpStatus = useCallback(() => {
    fetch(`${API}/api/client/dashboard/gbp/status`, { headers: getHeaders() })
      .then(r => r.json())
      .then(d => setGbpStatus(d))
      .catch(console.error)
      .finally(() => setLoadingGbp(false));
  }, [getHeaders]);

  const fetchGbpReviews = useCallback(() => {
    fetch(`${API}/api/client/dashboard/gbp/reviews`, { headers: getHeaders() })
      .then(r => r.json())
      .then(d => setGbpReviews(d))
      .catch(console.error);
  }, [getHeaders]);

  const fetchScheduledPosts = useCallback(() => {
    fetch(`${API}/api/client/dashboard/gbp/posts/scheduled`, { headers: getHeaders() })
      .then(r => r.json())
      .then(d => setScheduledPosts(d.posts || []))
      .catch(console.error);
  }, [getHeaders]);

  useEffect(() => { fetchGbpStatus(); }, [fetchGbpStatus]);
  useEffect(() => { if (gbpStatus?.connected) { fetchGbpReviews(); fetchScheduledPosts(); } }, [gbpStatus, fetchGbpReviews, fetchScheduledPosts]);

  const connectGBP = () => {
    window.location.href = `${API}/api/auth/google/gbp/authorize?client_slug=${client?.slug || ''}`;
  };

  const disconnectGBP = async () => {
    if (!window.confirm('Disconnect Google Business Profile?')) return;
    await fetch(`${API}/api/client/dashboard/gbp/disconnect`, { method: 'DELETE', headers: getHeaders() });
    setGbpStatus({ connected: false });
    setGbpReviews(null);
    setScheduledPosts([]);
  };

  const disconnectAndReconnect = async () => {
    await fetch(`${API}/api/client/dashboard/gbp/disconnect`, { method: 'DELETE', headers: getHeaders() });
    setGbpStatus({ connected: false });
    // Immediately start OAuth flow
    window.location.href = `${API}/api/auth/google/gbp/authorize?client_slug=${client?.slug || ''}`;
  };

  const fetchPastAudits = useCallback(() => {
    fetch(`${API}/api/client/gbp/audits`, { headers: getHeaders() })
      .then(r => r.json())
      .then(d => {
        const audits = d.audits || [];
        setPastAudits(audits);
        if (audits.length > 0) setAudit(audits[0]);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [getHeaders]);

  useEffect(() => { fetchPastAudits(); }, [fetchPastAudits]);

  const runAudit = async () => {
    setRunning(true);
    try {
      const res = await fetch(`${API}/api/client/gbp/audit`, { method: 'POST', headers: getHeaders() });
      const d = await res.json();
      if (res.ok) { setAudit(d); fetchPastAudits(); }
      else alert(d.detail || 'Audit failed');
    } catch (e) { console.error(e); }
    finally { setRunning(false); }
  };

  // ── Post Scheduler Handlers ──
  const handleSubmitPost = async (postNow) => {
    if (!postForm.summary.trim()) return alert('Post content is required');
    if (!postNow && !postForm.scheduled_for) return alert('Please select a date and time for scheduling');
    setPosting(true);
    try {
      const payload = {
        summary: postForm.summary,
        post_type: postForm.post_type,
        cta_type: postForm.cta_type || null,
        cta_url: postForm.cta_url || null,
        post_now: postNow,
        scheduled_for: postNow ? null : new Date(postForm.scheduled_for).toISOString(),
      };
      const res = await fetch(`${API}/api/client/dashboard/gbp/posts/schedule`, {
        method: 'POST',
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const d = await res.json();
      if (res.ok) {
        setPostForm({ summary: '', post_type: 'STANDARD', cta_type: '', cta_url: '', scheduled_for: '' });
        fetchScheduledPosts();
        alert(d.message || 'Done!');
      } else {
        alert(d.detail || 'Failed');
      }
    } catch (e) { console.error(e); alert('Network error'); }
    finally { setPosting(false); }
  };

  const handleEditPost = async () => {
    if (!editingPost) return;
    setPosting(true);
    try {
      const payload = {
        summary: postForm.summary,
        post_type: postForm.post_type,
        cta_type: postForm.cta_type || null,
        cta_url: postForm.cta_url || null,
        scheduled_for: postForm.scheduled_for ? new Date(postForm.scheduled_for).toISOString() : undefined,
      };
      const res = await fetch(`${API}/api/client/dashboard/gbp/posts/scheduled/${editingPost}`, {
        method: 'PUT',
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const d = await res.json();
      if (res.ok) {
        setEditingPost(null);
        setPostForm({ summary: '', post_type: 'STANDARD', cta_type: '', cta_url: '', scheduled_for: '' });
        fetchScheduledPosts();
      } else alert(d.detail || 'Failed');
    } catch (e) { console.error(e); }
    finally { setPosting(false); }
  };

  const handleCancelPost = async (postId) => {
    if (!window.confirm('Cancel this scheduled post?')) return;
    try {
      const res = await fetch(`${API}/api/client/dashboard/gbp/posts/scheduled/${postId}`, {
        method: 'DELETE', headers: getHeaders(),
      });
      const d = await res.json();
      if (res.ok) fetchScheduledPosts();
      else alert(d.detail || 'Failed');
    } catch (e) { console.error(e); }
  };

  const startEditPost = (post) => {
    setEditingPost(post.post_id);
    setPostForm({
      summary: post.summary,
      post_type: post.post_type || 'STANDARD',
      cta_type: post.cta_type || '',
      cta_url: post.cta_url || '',
      scheduled_for: post.scheduled_for ? new Date(post.scheduled_for).toISOString().slice(0, 16) : '',
    });
  };

  if (loading) return <div className="text-gray-400 animate-pulse py-12 text-center">{t('gbpAudit.loading')}</div>;

  return (
    <div className="space-y-6" data-testid="gbp-audit-tab">
      {/* GBP Connection Banner */}
      {!loadingGbp && (
        gbpStatus?.connected ? (
          <div className="space-y-2">
            <Card className={`border ${gbpStatus.warning ? 'border-amber-300 bg-amber-50' : 'border-green-200 bg-green-50'}`}>
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className={`w-5 h-5 ${gbpStatus.warning ? 'text-amber-500' : 'text-green-600'}`} />
                  <div>
                    <p className={`text-sm font-semibold ${gbpStatus.warning ? 'text-amber-800' : 'text-green-800'}`}>Google Business Profile Connected</p>
                    <p className={`text-xs ${gbpStatus.warning ? 'text-amber-600' : 'text-green-600'}`}>
                      {gbpStatus.accounts?.length || 0} account(s) linked
                      {gbpStatus.connected_at && ` — since ${new Date(gbpStatus.connected_at).toLocaleDateString()}`}
                    </p>
                  </div>
                </div>
                <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700 hover:bg-red-50 text-xs" onClick={disconnectGBP} data-testid="disconnect-gbp-btn">
                  <Unlink className="w-3 h-3 mr-1" /> Disconnect
                </Button>
              </CardContent>
            </Card>
            {gbpStatus.warning && (
              <Card className="border border-amber-300 bg-amber-50">
                <CardContent className="p-4 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-amber-800">Action Required</p>
                    <p className="text-xs text-amber-700 mt-1 leading-relaxed">{gbpStatus.warning}</p>
                    <Button size="sm" className="mt-2 bg-amber-600 hover:bg-amber-700 text-white text-xs" onClick={disconnectAndReconnect}>
                      Disconnect & Reconnect with Correct Account
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        ) : (
          <Card className="border border-blue-200 bg-gradient-to-br from-blue-50 to-white" data-testid="gbp-connect-guide">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900">Connect Your Google Business Profile</h3>
                  <p className="text-xs text-gray-500">Takes less than 60 seconds</p>
                </div>
              </div>

              <div className="space-y-3 mb-5">
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center mt-0.5">1</span>
                  <div>
                    <p className="text-sm font-medium text-gray-800">Click "Connect with Google" below</p>
                    <p className="text-xs text-gray-500">You'll be taken to Google's sign-in page</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center mt-0.5">2</span>
                  <div>
                    <p className="text-sm font-medium text-gray-800">Sign in with your business Google account</p>
                    <p className="text-xs text-gray-500">Use the same email you use to manage your Google Business listing (the one you log into at <a href="https://business.google.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">business.google.com</a>)</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center mt-0.5">3</span>
                  <div>
                    <p className="text-sm font-medium text-gray-800">Allow access</p>
                    <p className="text-xs text-gray-500">Grant permission so we can manage posts, respond to reviews, and optimize your profile on your behalf</p>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4 flex-wrap">
                <Button onClick={connectGBP} className="bg-blue-600 hover:bg-blue-700 text-white text-sm px-6 py-2.5 rounded-lg font-semibold shadow-md" data-testid="connect-gbp-btn">
                  <svg viewBox="0 0 24 24" className="w-4 h-4 mr-2" fill="none">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#fff"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#fff" fillOpacity="0.7"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#fff" fillOpacity="0.5"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#fff" fillOpacity="0.8"/>
                  </svg>
                  Connect with Google
                </Button>
              </div>

              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-xs text-amber-800">
                  <strong>Not sure which Google account to use?</strong> It's the email address you use to log into your Google Business Profile at <a href="https://business.google.com" target="_blank" rel="noopener noreferrer" className="underline font-medium">business.google.com</a>. If someone else manages it for you, ask them to connect it from this page.
                </p>
              </div>

              <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { icon: Star, label: 'Review Management', desc: 'Read & respond to reviews' },
                  { icon: Send, label: 'Post Updates', desc: 'Schedule posts to your listing' },
                  { icon: Search, label: 'Profile Audit', desc: 'AI-powered optimization tips' },
                  { icon: RefreshCw, label: 'Auto-Posting', desc: 'AI posts on your behalf' },
                ].map((f, i) => (
                  <div key={i} className="text-center p-2.5 bg-white rounded-lg border border-gray-100">
                    <f.icon className="w-4 h-4 text-blue-600 mx-auto mb-1" />
                    <p className="text-xs font-medium text-gray-800">{f.label}</p>
                    <p className="text-[10px] text-gray-400">{f.desc}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )
      )}

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Google Business Profile</h2>
          <p className="text-xs text-gray-400">Manage reviews, post updates, and audit your GBP</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
            {gbpStatus?.connected && (
              <>
                <button onClick={() => setTab('posts')} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${tab === 'posts' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-testid="tab-posts">Posts</button>
                <button onClick={() => setTab('reviews')} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${tab === 'reviews' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-testid="tab-reviews">Reviews</button>
                <button onClick={() => setTab('phone')} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${tab === 'phone' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-testid="tab-phone">Phone Setup</button>
              </>
            )}
            <button onClick={() => setTab('audit')} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${tab === 'audit' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-testid="tab-audit">Audit</button>
            <button onClick={() => setTab('history')} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${tab === 'history' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-testid="tab-history">History</button>
          </div>
          <Button onClick={runAudit} disabled={running} className="bg-amber-600 hover:bg-amber-700 text-white text-xs" data-testid="run-gbp-audit">
            <Search className="h-3.5 w-3.5 mr-1.5" /> {running ? 'Auditing...' : 'Run Audit'}
          </Button>
        </div>
      </div>

      {/* ═══════════ Posts Tab ═══════════ */}
      {tab === 'posts' && gbpStatus?.connected && (
        <div className="space-y-6">
          {/* Composer */}
          <Card className="bg-white border-gray-200">
            <CardContent className="p-6">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-4">
                <PenTool className="w-4 h-4 text-blue-500" />
                {editingPost ? 'Edit Scheduled Post' : 'Compose New Post'}
              </h3>
              <div className="space-y-4">
                <div>
                  <textarea
                    data-testid="post-composer-text"
                    value={postForm.summary}
                    onChange={e => setPostForm(f => ({ ...f, summary: e.target.value }))}
                    placeholder="Write your Google Business post... Share updates, promotions, or events with your customers."
                    className="w-full border border-gray-200 rounded-lg p-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 resize-none bg-gray-50"
                    rows={4}
                    maxLength={1500}
                  />
                  <p className="text-xs text-gray-400 mt-1 text-right">{postForm.summary.length}/1500</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Post Type</label>
                    <select
                      data-testid="post-type-select"
                      value={postForm.post_type}
                      onChange={e => setPostForm(f => ({ ...f, post_type: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                    >
                      {POST_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Call to Action</label>
                    <select
                      data-testid="post-cta-select"
                      value={postForm.cta_type}
                      onChange={e => setPostForm(f => ({ ...f, cta_type: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                    >
                      {CTA_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  {postForm.cta_type && (
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">CTA URL</label>
                      <input
                        data-testid="post-cta-url"
                        type="url"
                        value={postForm.cta_url}
                        onChange={e => setPostForm(f => ({ ...f, cta_url: e.target.value }))}
                        placeholder="https://..."
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                      />
                    </div>
                  )}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Schedule For</label>
                    <input
                      data-testid="post-schedule-datetime"
                      type="datetime-local"
                      value={postForm.scheduled_for}
                      onChange={e => setPostForm(f => ({ ...f, scheduled_for: e.target.value }))}
                      min={new Date().toISOString().slice(0, 16)}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                  <span className="text-xs text-gray-400 flex items-center gap-1"><Coins className="w-3 h-3" /> Included with subscription</span>
                  <div className="flex gap-2">
                    {editingPost ? (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => { setEditingPost(null); setPostForm({ summary: '', post_type: 'STANDARD', cta_type: '', cta_url: '', scheduled_for: '' }); }} className="text-xs text-gray-500" data-testid="cancel-edit-btn">Cancel</Button>
                        <Button size="sm" onClick={handleEditPost} disabled={posting} className="bg-blue-600 hover:bg-blue-700 text-white text-xs" data-testid="save-edit-btn">
                          {posting ? 'Saving...' : 'Save Changes'}
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleSubmitPost(false)}
                          disabled={posting || !postForm.summary.trim()}
                          className="text-xs border-blue-200 text-blue-600 hover:bg-blue-50"
                          data-testid="schedule-post-btn"
                        >
                          <Calendar className="w-3 h-3 mr-1" /> {posting ? 'Scheduling...' : 'Schedule'}
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleSubmitPost(true)}
                          disabled={posting || !postForm.summary.trim()}
                          className="bg-blue-600 hover:bg-blue-700 text-white text-xs"
                          data-testid="post-now-btn"
                        >
                          <Send className="w-3 h-3 mr-1" /> {posting ? 'Posting...' : 'Post Now'}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Scheduled Posts List */}
          <Card className="bg-white border-gray-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gray-500" /> Post History
                </h3>
                <Button size="sm" variant="ghost" onClick={fetchScheduledPosts} className="text-xs text-gray-500" data-testid="refresh-posts-btn">
                  <RefreshCw className="w-3 h-3 mr-1" /> Refresh
                </Button>
              </div>
              {scheduledPosts.length === 0 ? (
                <div className="text-center py-8">
                  <Calendar className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No posts yet</p>
                  <p className="text-xs text-gray-400 mt-1">Create your first post above to get started.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {scheduledPosts.map(post => (
                    <div key={post.post_id} className="bg-gray-50 rounded-lg p-4 border border-gray-100" data-testid={`post-item-${post.post_id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                            <Badge className={`text-[10px] border ${POST_STATUS_STYLES[post.status] || POST_STATUS_STYLES.scheduled}`}>
                              {post.status}
                            </Badge>
                            <Badge className="bg-gray-100 text-gray-600 text-[10px]">{POST_TYPES.find(t => t.value === post.post_type)?.label || post.post_type}</Badge>
                            {post.cta_type && (
                              <Badge className="bg-purple-50 text-purple-600 text-[10px] flex items-center gap-0.5">
                                <ExternalLink className="w-2.5 h-2.5" /> {CTA_TYPES.find(t => t.value === post.cta_type)?.label || post.cta_type}
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-800 line-clamp-2">{post.summary}</p>
                          <div className="flex items-center gap-3 mt-2 text-[10px] text-gray-400">
                            {post.scheduled_for && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> Scheduled: {new Date(post.scheduled_for).toLocaleString()}</span>}
                            {post.published_at && <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-green-500" /> Published: {new Date(post.published_at).toLocaleString()}</span>}
                            {post.error && <span className="text-red-400">Error: {typeof post.error === 'string' ? post.error.slice(0, 80) : 'Publishing failed'}</span>}
                          </div>
                        </div>
                        {post.status === 'scheduled' && (
                          <div className="flex gap-1 flex-shrink-0">
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-gray-400 hover:text-blue-600" onClick={() => startEditPost(post)} data-testid={`edit-post-${post.post_id}`}>
                              <Pencil className="w-3.5 h-3.5" />
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-gray-400 hover:text-red-600" onClick={() => handleCancelPost(post.post_id)} data-testid={`cancel-post-${post.post_id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* ═══════════ Reviews Tab ═══════════ */}
      {tab === 'reviews' && gbpStatus?.connected && (
        <Card className="bg-white border-gray-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2"><Star className="w-4 h-4 text-yellow-500" /> Google Reviews</h3>
              {gbpReviews && (
                <div className="flex items-center gap-3 text-xs">
                  <span className="text-gray-500">{gbpReviews.total} total reviews</span>
                  <Badge className="bg-yellow-50 text-yellow-700 text-xs">{gbpReviews.average_rating} avg</Badge>
                </div>
              )}
            </div>
            {!gbpReviews || gbpReviews.reviews?.length === 0 ? (
              <div className="text-center py-8">
                <MessageSquare className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No reviews loaded yet</p>
                <p className="text-xs text-gray-400 mt-1">Reviews will appear once GBP data syncs. If newly connected, it may take a moment.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {gbpReviews.reviews.map((r, i) => (
                  <div key={i} className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900">{r.reviewer?.displayName || 'Anonymous'}</span>
                        <div className="flex">
                          {[1,2,3,4,5].map(s => (
                            <Star key={s} className={`w-3.5 h-3.5 ${s <= (r.starRating === 'FIVE' ? 5 : r.starRating === 'FOUR' ? 4 : r.starRating === 'THREE' ? 3 : r.starRating === 'TWO' ? 2 : 1) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
                          ))}
                        </div>
                      </div>
                      <span className="text-xs text-gray-400">{r.createTime ? new Date(r.createTime).toLocaleDateString() : ''}</span>
                    </div>
                    {r.comment && <p className="text-sm text-gray-600 mb-2">{r.comment}</p>}
                    {r.reviewReply ? (
                      <div className="bg-blue-50 rounded p-2 border-l-2 border-blue-400 mt-2">
                        <p className="text-xs text-blue-800"><strong>Your reply:</strong> {r.reviewReply.comment}</p>
                      </div>
                    ) : (
                      <Button size="sm" variant="ghost" className="text-xs text-blue-600 hover:text-blue-700 h-7 px-2">
                        <Send className="w-3 h-3 mr-1" /> Reply
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ═══════════ Phone Setup Tab ═══════════ */}
      {tab === 'phone' && gbpStatus?.connected && (
        <GBPPhoneSetup getHeaders={getHeaders} />
      )}

      {/* ═══════════ Audit Tab ═══════════ */}
      {tab === 'audit' && audit && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className={`bg-gradient-to-br ${GRADE_COLORS[audit.grade] || GRADE_COLORS.C} border-0 lg:col-span-1`}>
              <CardContent className="pt-8 pb-8 text-center">
                <p className="text-gray-900/70 text-xs uppercase tracking-wider mb-2">GBP Score</p>
                <p className="text-7xl font-black text-gray-900" data-testid="gbp-score">{audit.overall_score}</p>
                <p className="text-4xl font-black text-gray-900/30 mt-1">/ 100</p>
                <div className="mt-4 inline-flex items-center gap-2 bg-black/20 rounded-full px-4 py-2">
                  <span className="text-2xl font-black text-gray-900">Grade: {audit.grade}</span>
                </div>
                {audit.data_source && (
                  <div className="mt-3">
                    <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full ${audit.data_source === 'google_api' ? 'bg-black/15 text-gray-900' : 'bg-black/10 text-gray-900/70'}`} data-testid="audit-data-source">
                      {audit.data_source === 'google_api' ? (
                        <><CheckCircle2 className="w-2.5 h-2.5" /> Live Google Data</>
                      ) : 'Local Profile Data'}
                    </span>
                  </div>
                )}
                <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                  <div><p className="text-2xl font-bold text-gray-900">{audit.complete_count}</p><p className="text-[10px] text-gray-900/60">Complete</p></div>
                  <div><p className="text-2xl font-bold text-gray-900">{audit.partial_count}</p><p className="text-[10px] text-gray-900/60">Partial</p></div>
                  <div><p className="text-2xl font-bold text-gray-900">{audit.missing_count}</p><p className="text-[10px] text-gray-900/60">Missing</p></div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white border-gray-200 lg:col-span-2">
              <CardContent className="pt-5">
                <h3 className="text-gray-900 font-medium text-sm mb-4">Profile Checklist</h3>
                <div className="space-y-2">
                  {(audit.items || []).map((item, i) => {
                    const Icon = STATUS_ICON[item.status] || AlertCircle;
                    const color = STATUS_COLOR[item.status] || 'text-gray-400';
                    return (
                      <div key={i} className="flex items-start gap-3 py-2 border-b border-gray-200/30 last:border-0">
                        <Icon className={`h-4 w-4 mt-0.5 ${color} flex-shrink-0`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="text-sm text-gray-900">{item.label}</p>
                            <span className="text-xs text-gray-500">{item.score}/{item.max_score} pts</span>
                          </div>
                          {item.status !== 'complete' && <p className="text-xs text-gray-400 mt-0.5">{item.tip}</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
          {audit.recommendations && audit.recommendations.length > 0 && (
            <Card className="bg-white border-gray-200">
              <CardContent className="pt-5">
                <h3 className="text-gray-900 font-medium text-sm mb-4 flex items-center gap-2">
                  <Lightbulb className="h-4 w-4 text-amber-400" /> AI Recommendations
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {audit.recommendations.map((rec, i) => (
                    <div key={i} className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                      <p className="text-sm text-gray-900 font-medium mb-1">{rec.title}</p>
                      {rec.reason && <p className="text-xs text-amber-400/80 mb-2">{rec.reason}</p>}
                      {rec.action && <p className="text-xs text-gray-400">{rec.action}</p>}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {tab === 'audit' && !audit && (
        <Card className="bg-white border-gray-200">
          <CardContent className="py-16 text-center">
            <Search className="h-12 w-12 text-gray-600 mx-auto mb-4" />
            <h3 className="text-gray-900 font-medium text-lg mb-2">No Audit Yet</h3>
            <p className="text-gray-400 text-sm mb-6">Run your first GBP audit to see how your profile scores and get AI-powered recommendations.</p>
            <Button onClick={runAudit} disabled={running} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="run-first-audit">
              <Coins className="h-4 w-4 mr-2" /> {running ? 'Running...' : 'Run Audit'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* ═══════════ History Tab ═══════════ */}
      {tab === 'history' && (
        <div className="space-y-3">
          {pastAudits.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No audit history</p>
          ) : pastAudits.map((a, i) => (
            <Card key={i} className="bg-white border-gray-200 cursor-pointer hover:border-gray-300 transition-colors"
              onClick={() => { setAudit(a); setTab('audit'); }}>
              <CardContent className="pt-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${GRADE_COLORS[a.grade]} flex items-center justify-center`}>
                    <span className="text-xl font-black text-gray-900">{a.grade}</span>
                  </div>
                  <div>
                    <p className="text-gray-900 font-medium text-sm">Score: {a.overall_score}/100</p>
                    <p className="text-[10px] text-gray-500">{a.created_at ? new Date(a.created_at).toLocaleString() : ''}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Badge className="bg-green-50 text-green-600 text-[10px]">{a.complete_count} complete</Badge>
                  <Badge className="bg-red-50 text-red-600 text-[10px]">{a.missing_count} missing</Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
